import { useMutation } from "@tanstack/react-query";
import { GetCardSignaturesResponse } from "tng-address-collection-shared/types/apiTypes.ts";
import { useAuthentication } from "../auth/useAuthentication.ts";
import { queryKeys } from "./queryKeys.ts";
import { useCancelCardListQueries } from "./useToggleCardSignatureCache.ts";
import { useToggleOtherCardSignatureCache } from "./useToggleOtherCardSignatureCache.ts";

export const useToggleOtherCardSignature = (
  cardId: string | undefined,
  projectId: string,
) => {
  const { getToken } = useAuthentication();
  const toggleOtherCardSignatureCache =
    useToggleOtherCardSignatureCache(projectId);
  const cancelCardListQueries = useCancelCardListQueries();

  return useMutation({
    mutationKey: ["cards", cardId, "signatures", "other"],
    onMutate: async () => {
      await cancelCardListQueries(projectId);
    },
    mutationFn: async ({
      uidNumber,
      action,
    }: {
      uidNumber: number;
      action: "add" | "remove";
    }) => {
      if (!cardId || !uidNumber) {
        throw new Error("Card or signer missing");
      }

      const token = await getToken();
      const response = await fetch(
        `/api/projects/${projectId}/cards/${cardId}/signatures/${uidNumber}`,
        {
          method: action === "remove" ? "DELETE" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Changing the signature failed");
      }
    },
    onSuccess: (_1, { uidNumber, action }, _3, context) => {
      if (cardId) {
        toggleOtherCardSignatureCache(cardId, uidNumber, action);
      }
      return context.client.setQueryData(
        queryKeys.cardSignatures(projectId, cardId),
        (queryData: GetCardSignaturesResponse | undefined) => {
          if (!queryData) return queryData;
          const signaturesWithoutUidNumber = queryData.signatures.filter(
            (x) => x.uidNumber !== uidNumber,
          );
          return {
            ...queryData,
            signatures:
              action === "remove"
                ? signaturesWithoutUidNumber
                : signaturesWithoutUidNumber.concat({ uidNumber }),
          };
        },
      );
    },
  });
};
